import { service } from '@ember/service';
import Route from '@ember/routing/route';
import { findRecord } from '@warp-drive/utilities/json-api';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type EmployeeService from 'tio-employee/services/employee';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ReimaginedContributionHistoryModel } from 'tio-employee/components/slr/contribution-history';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type LrapEnrollmentService from 'tio-employee/services/lrap-enrollment';
import type { LrapEnrollmentStatus } from 'tio-data/requests/enrollment';

export default class AuthenticatedSlrDashboard extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare lrapEnrollment: LrapEnrollmentService;

  private loadLoanStandingVerification = async () => {
    try {
      // reload, like the person fetch below: the verdict is scoped to the
      // account receiving contributions, and nothing invalidates its document
      // when that account changes. Served from the CachePolicy soft window a
      // `router.refresh()` would hand back the PREVIOUS account's verdict, so
      // switching to an account that needs attention showed no alert until a
      // hard browser refresh built a fresh store. Same trap the post-upload
      // refresh works around by reloading before it refreshes the route.
      return await this.slrLoanStandingVerification.fetchCurrentState({ reload: true });
    } catch (error) {
      console.warn('[SLR loan standing] Failed to load current audit state', error);
      return null;
    }
  };

  private loadLrapEnrollmentStatus = async () => {
    return this.lrapEnrollment.fetchStatus();
  };

  async model() {
    // This is a hack to make sure that contributions data is loaded after an doc upload for DEV-4807
    // TODO: Employee contributions data is a good candidate for updating to request manager to improve this flow
    if (this.employee.contributionsState.isFinished) {
      this.employee.contributionsState.retry();
    }
    if (this.employee.futurePayments.isFinished) {
      this.employee.futurePayments.retry();
    }
    const personId = this.sessionContext.user.person.id;
    if (!personId) {
      throw new Error('Cannot load SLR dashboard without a person id.');
    }

    const employee = this.sessionContext.currentEmployee;
    // Without an employment this is an unsaved placeholder, which has no limits to read.
    const limitsStatusPromise = employee?.id
      ? this.tuitionAssistance.getV2SlrLimitsStatus(employee, employee.plan?.id ?? undefined)
      : undefined;

    // Loaded BEFORE the rest, not alongside it. The verdict query is scoped to
    // `person.accountReceivingContributionsId`, which fetchCurrentState reads
    // synchronously off the session record — and the drawer's
    // contribution-account PATCH is SkipCache, so it never writes that field
    // back. This reload is the only thing that does. Started in the same
    // Promise.all as the verdict it could not land first, so an account switch
    // kept querying the PREVIOUS account (and reload: true only fetched the
    // wrong account's verdict faster). One serialized request, for correctness.
    const person = await this.store
      .request(
        findRecord<PersonModel>('person', personId, {
          include: 'accounts.financial-institution,accounts.loans,accounts.postal-address',
          reload: true,
        })
      )
      .then((r) => (r as { content: { data: PersonModel } }).content.data);

    const [
      contributionHistory,
      employerContributions,
      section127Info,
      loanStandingVerification,
      limitsStatus,
      lrapEnrollmentStatus,
    ] = await Promise.all([
      this.reimaginedData.fetchContributionHistory(),
      this.reimaginedData.fetchEmployerContributions(),
      this.reimaginedData.fetchSection127Info(),
      this.loadLoanStandingVerification(),
      limitsStatusPromise,
      this.loadLrapEnrollmentStatus(),
    ]);

    const hasStrategyFinderResults = (employee?.strategyFinderResults?.length ?? 0) > 0;

    const reimagined: ReimaginedContributionHistoryModel = {
      contributionHistory,
      employerContributions,
      person,
      section127Info,
      hasStrategyFinderResults,
      limitsStatus,
    };

    return { person, reimagined, loanStandingVerification, lrapEnrollmentStatus };
  }
}

export interface AuthenticatedSlrDashboardModel {
  person: PersonModel;
  reimagined: ReimaginedContributionHistoryModel;
  loanStandingVerification: SlrLoanStandingAuditState | null;
  lrapEnrollmentStatus: LrapEnrollmentStatus | null;
}
